import React from "react";
import { styled, Collapse, Grid2 as Grid, IconButton, TableCell, TableRow, Typography, Badge } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import HistoryIcon from "@mui/icons-material/History";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { isLineItemCancelled, isLineItemOverReceived, quantityOverReceived } from "../../util/lineItemUtil";
import { Alert } from "@mui/material";
import { BlockOutlined, PrintOutlined } from "@mui/icons-material";
import { getAvailabileForCustomerOrderQty } from "../../util/customerOrderUtil";
import {
	cancelledLineItemMessage,
	lineItemNotOnInvoiceMessage,
	overReceivedLineItemMessage,
	overReceivedTransferLineItemMessage,
} from "../../util/messagingUtil";
import EditLocationOutlined from "@mui/icons-material/EditLocationOutlined";
import { useStyles } from "./Invoice.styles";
import { PriorityStockFlag } from "../PriorityStock/PriorityStockFlag";
import { InvoiceLineItemRowProps } from "./types";

const StyledTableCell = styled(TableCell)({
	padding: 0,
});

const InvoiceLineItemRow: React.FC<InvoiceLineItemRowProps> = ({
	invoiceLineItem,
	onEditClick,
	onRemoveClick,
	onHistoryClick,
	isHardwareView,
	isDisableHistoryButton,
	creditRequest,
	isEditable,
	isLocationBayEditable,
	onLocationNameChange,
	priorityFlag,
	priorityReason,
	isArrivalsFeatureEnabled,
	isPriorityStockEnabled,
	hideOverReceivedMessage,
	hideReceivedRemainEditColumns,
}) => {
	const [open, setOpen] = React.useState(false);
	const classes = useStyles();

	const detailColSpan = hideReceivedRemainEditColumns ? 2 : 5;

	const isOverReceived = isLineItemOverReceived(invoiceLineItem);
	const amountOverReceived = quantityOverReceived(invoiceLineItem);
	const isCancelled = isLineItemCancelled(invoiceLineItem.purchaseOrderItem);

	const getPercentComplete = () => {
		return (invoiceLineItem.received / invoiceLineItem.invoiced) * 100;
	};

	const getTheme = () => {
		if (isEditable) {
			if (invoiceLineItem.isExtraFromReceive) {
				return { background: classes.mismatch, border: classes.mismatchBorder };
			}
			// Part Cancelled
			if (isCancelled) {
				return { background: classes.warn, border: classes.warnBorder };
			}
			if (isOverReceived) {
				return { background: classes.over, border: classes.overBorder };
			}
		}

		// Progressive
		const percent = getPercentComplete();
		if (percent === 100) {
			return { background: classes.full, border: classes.fullBorder };
		} else if (percent > 0) {
			return { background: classes.partial, border: classes.partialBorder };
		} else {
			return { background: classes.none, border: classes.noneBorder };
		}
	};

	const getRemainderOrCredited = () => {
		if (creditRequest?.lineItems) {
			return creditRequest.lineItems
				.filter((li) => li.sku === invoiceLineItem.sku)
				.reduce((t, { quantity }) => t + quantity, 0);
		}
		return invoiceLineItem.remainder;
	};

	const getLineItemMessaging = () => {
		if (!isEditable) {
			return;
		}
		if (isCancelled) {
			return (
				<Grid container size={12}>
					<Alert icon={<BlockOutlined fontSize="inherit" />} severity="error">
						{cancelledLineItemMessage(invoiceLineItem.sku)}
					</Alert>
				</Grid>
			);
		}
		if (isOverReceived && !hideOverReceivedMessage) {
			return invoiceLineItem.transferItem ? (
				<Grid container size={12}>
					<Alert severity="warning">
						{overReceivedTransferLineItemMessage(
							invoiceLineItem.sku,
							amountOverReceived,
							invoiceLineItem.transferItem.toLocation.locationName,
							invoiceLineItem.transferItem.toLocation.locationId
						)}
					</Alert>
				</Grid>
			) : (
				<Grid container size={12}>
					<Alert severity="warning">{overReceivedLineItemMessage(invoiceLineItem.sku)}</Alert>
				</Grid>
			);
		}
		if (invoiceLineItem.isExtraFromReceive) {
			return (
				<Grid container size={12}>
					<Alert severity="warning" className={classes.alertMismatchColor}>
						{lineItemNotOnInvoiceMessage(invoiceLineItem.sku)}
					</Alert>
				</Grid>
			);
		}
	};
	return (
		<React.Fragment>
			<TableRow className={getTheme().background}>
				<StyledTableCell className={getTheme().border} style={{ width: "40px" }}>
					<IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
						{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</StyledTableCell>
				<StyledTableCell>
					<Badge
						overlap="rectangular"
						classes={{ badge: classes.badge }}
						className={classes.badgePadding}
						badgeContent={getAvailabileForCustomerOrderQty(
							invoiceLineItem.invoiced,
							invoiceLineItem.originalReceived,
							invoiceLineItem.inventory?.quantity
						)}
					>
						<Typography variant="body2">{invoiceLineItem.sku}</Typography>
					</Badge>
				</StyledTableCell>
				<StyledTableCell align="center">
					<Typography variant="body2">{invoiceLineItem.invoiced}</Typography>
				</StyledTableCell>
				{!hideReceivedRemainEditColumns && (
					<>
						<StyledTableCell align="center">
							<Typography variant="body2">{invoiceLineItem.received}</Typography>
						</StyledTableCell>
						<StyledTableCell align="center">
							<Typography variant="body2">{getRemainderOrCredited()}</Typography>
						</StyledTableCell>
						<StyledTableCell align="center">
							{invoiceLineItem.isExtraFromReceive && (
								<IconButton aria-label="edit row" size="small" onClick={() => onRemoveClick(invoiceLineItem)}>
									<HighlightOffIcon />
								</IconButton>
							)}
							{!invoiceLineItem.isExtraFromReceive && (
								<IconButton aria-label="edit row" size="small" onClick={() => onEditClick(invoiceLineItem)}>
									<EditIcon />
								</IconButton>
							)}
						</StyledTableCell>
					</>
				)}
			</TableRow>
			<TableRow>
				<StyledTableCell className={getTheme().border}>
					{isLocationBayEditable && (
						<IconButton
							aria-label="Change location bay"
							size="small"
							onClick={() => onLocationNameChange(invoiceLineItem.inboundLocation, invoiceLineItem.sku)}
						>
							<EditLocationOutlined />
						</IconButton>
					)}
				</StyledTableCell>
				<StyledTableCell colSpan={detailColSpan}>
					<Grid container>
						<Grid container size={12}>
							<Grid size={3}>
								<Typography variant="caption" color="textSecondary">
									Model
								</Typography>
							</Grid>
							<Grid size={9}>
								<Typography variant="body2" noWrap>
									{invoiceLineItem.model}
								</Typography>
							</Grid>
						</Grid>
						{invoiceLineItem.barcode && (
							<Grid container size={12}>
								<Grid size={3}>
									<Typography variant="caption" color="textSecondary">
										Barcode
									</Typography>
								</Grid>
								<Grid size={9}>
									<Typography variant="body2" noWrap>
										{invoiceLineItem.barcode}
									</Typography>
								</Grid>
							</Grid>
						)}
						<Grid container size={12}>
							<Grid size={3}>
								<Typography variant="caption" color="textSecondary">
									Desc
								</Typography>
							</Grid>
							<Grid size={9}>
								<Typography variant="body2" noWrap className={classes.productGroupAndDescriptionWidth}>
									{invoiceLineItem.description}
								</Typography>
							</Grid>
						</Grid>
						{!isHardwareView && (
							<Grid container size={12}>
								<Grid size={3}>
									<Typography variant="caption" color="textSecondary">
										Artist
									</Typography>
								</Grid>
								<Grid size={9}>
									<Typography variant="body2" noWrap>
										{invoiceLineItem.artist}
									</Typography>
								</Grid>
							</Grid>
						)}
						{invoiceLineItem.productGroup && (
							<Grid container size={12}>
								<Grid size={3}>
									<Typography variant="caption" color="textSecondary">
										Group
									</Typography>
								</Grid>
								<Grid size={8}>
									<Typography variant="body2" noWrap className={classes.productGroupAndDescriptionWidth}>
										{invoiceLineItem.productGroup}
									</Typography>
								</Grid>
								{invoiceLineItem.hasLabel && (
									<Grid size={1}>
										<PrintOutlined className={classes.printLabelIcon} />
									</Grid>
								)}
							</Grid>
						)}
						{!isHardwareView && (
							<Grid container size={12}>
								<Grid size={3}>
									<Typography variant="caption" color="textSecondary">
										Format
									</Typography>
								</Grid>
								<Grid size={9}>
									<Typography variant="body2" noWrap>
										{invoiceLineItem.format}
									</Typography>
								</Grid>
							</Grid>
						)}
						<Grid container size={12}>
							{invoiceLineItem.ticketPrice > 0 && (
								<Grid container size={6}>
									<Grid size={6}>
										<Typography variant="caption" color="textSecondary">
											Ticket
										</Typography>
									</Grid>
									<Grid size={6}>
										<Typography variant="body2">${invoiceLineItem.ticketPrice?.toFixed(2)}</Typography>
									</Grid>
								</Grid>
							)}
							{invoiceLineItem.promoPrice > 0 && (
								<Grid container size={6}>
									<Grid size={6}>
										<Typography variant="caption" color="textSecondary">
											Promo
										</Typography>
									</Grid>
									<Grid size={6}>
										<Typography variant="body2" className={classes.highlight}>
											${invoiceLineItem.promoPrice?.toFixed(2)}
										</Typography>
									</Grid>
								</Grid>
							)}
						</Grid>
						{priorityFlag && (isArrivalsFeatureEnabled || isPriorityStockEnabled) && (
							<Grid container size={12}>
								<Grid size={12}>
									<PriorityStockFlag flag={priorityFlag} />
								</Grid>
							</Grid>
						)}
						{priorityReason && (isArrivalsFeatureEnabled || isPriorityStockEnabled) && (
							<Grid container size={12}>
								<Grid size={12}>
									<Typography variant="caption" color="textSecondary">
										{priorityReason}
									</Typography>
								</Grid>
							</Grid>
						)}
						{getLineItemMessaging()}
					</Grid>
				</StyledTableCell>
			</TableRow>
			<TableRow>
				<StyledTableCell className={getTheme().border}></StyledTableCell>
				<StyledTableCell colSpan={detailColSpan}>
					<Collapse in={open} timeout="auto" unmountOnExit>
						<Grid container>
							<Grid container size={12}>
								<Grid size={3}>
									<Typography variant="caption" color="textSecondary">
										Amount
									</Typography>
								</Grid>
								<Grid size={3}>
									<Typography variant="body2" noWrap>
										${invoiceLineItem.amount?.toFixed(2)}
									</Typography>
								</Grid>
								<Grid size={3}>
									<Typography variant="caption" color="textSecondary">
										Unit Cost
									</Typography>
								</Grid>
								<Grid size={3}>
									<Typography variant="body2">${invoiceLineItem.unitCost?.toFixed(2)}</Typography>
								</Grid>
							</Grid>
							<Grid container size={12}>
								<Grid container size={6}>
									<Grid size={6}>
										<Typography variant="caption" color="textSecondary">
											GST
										</Typography>
									</Grid>
									<Grid size={6}>
										<Typography variant="body2">${invoiceLineItem.gst?.toFixed(2)}</Typography>
									</Grid>
								</Grid>
								<Grid container size={6} justifyContent="flex-end">
									<Grid>
										<IconButton
											disabled={isDisableHistoryButton}
											aria-label="edit row"
											size="small"
											onClick={() => onHistoryClick(invoiceLineItem)}
										>
											<HistoryIcon />
										</IconButton>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Collapse>
				</StyledTableCell>
			</TableRow>
		</React.Fragment>
	);
};

export default InvoiceLineItemRow;
